import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bus, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { driverLogin, adminLogin } from '../lib/api';
import { saveAuth } from '../lib/auth';
import { toast } from 'sonner';

const COMPANY_LOGO = 'https://customer-assets.emergentagent.com/job_coachcheck/artifacts/w12hb0ce_Screenshot_20260118_231725_Samsung%20Internet.jpg';

export default function LoginPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('driver');
  const [loading, setLoading] = useState(false);
  
  // Driver login state
  const [badgeNumber, setBadgeNumber] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];
  
  // Admin login state
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const handlePinChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    
    // Auto-advance to next input
    if (value && index < 3) {
      pinRefs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const handleDriverLogin = async (e) => {
    e.preventDefault();
    const pinString = pin.join('');
    
    if (!badgeNumber.trim()) {
      toast.error('Please enter your badge number');
      return;
    }
    if (pinString.length !== 4) {
      toast.error('Please enter your 4-digit PIN');
      return;
    }
    
    setLoading(true);
    try {
      const response = await driverLogin(badgeNumber.trim(), pinString);
      const { token, user_type, name, id } = response.data;
      saveAuth(token, { user_type, name, id });
      toast.success(`Welcome back, ${name}!`);
      navigate('/driver/checklist');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    
    if (!adminUsername.trim() || !adminPassword) {
      toast.error('Please enter username and password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await adminLogin(adminUsername.trim(), adminPassword);
      const { token, user_type, name, id } = response.data;
      saveAuth(token, { user_type, name, id });
      toast.success('Welcome to Admin Dashboard');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-blue-900 text-white py-4 px-6">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <img 
            src={COMPANY_LOGO} 
            alt="City Cabs" 
            className="h-12 w-12 rounded-lg object-cover bg-white"
          />
          <div>
            <h1 className="font-bold text-lg leading-tight">City Cabs</h1>
            <p className="text-blue-200 text-sm">Buses & Coaches Ltd</p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-slate-200 animate-fade-in">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-blue-900">Vehicle Check System</CardTitle>
            <CardDescription>Sign in to complete your walk-around check</CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="driver" className="gap-2" data-testid="driver-tab">
                  <Bus className="w-4 h-4" />
                  Driver
                </TabsTrigger>
                <TabsTrigger value="admin" className="gap-2" data-testid="admin-tab">
                  <ShieldCheck className="w-4 h-4" />
                  Admin
                </TabsTrigger>
              </TabsList>

              <TabsContent value="driver">
                <form onSubmit={handleDriverLogin} className="space-y-6">
                  <div className="form-group">
                    <label htmlFor="badge">Badge Number</label>
                    <Input
                      id="badge"
                      data-testid="badge-input"
                      placeholder="Enter your badge number"
                      value={badgeNumber}
                      onChange={(e) => setBadgeNumber(e.target.value)}
                      className="h-12 text-lg"
                    />
                  </div>

                  <div className="form-group">
                    <label>4-Digit PIN</label>
                    <div className="flex justify-center gap-3" data-testid="pin-inputs">
                      {pin.map((digit, index) => (
                        <input
                          key={index}
                          ref={pinRefs[index]}
                          type="password"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handlePinChange(index, e.target.value)}
                          onKeyDown={(e) => handlePinKeyDown(index, e)}
                          className="pin-input"
                          data-testid={`pin-input-${index}`}
                        />
                      ))}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-yellow-500 hover:bg-yellow-600 text-blue-950 font-bold text-lg btn-press"
                    disabled={loading}
                    data-testid="driver-login-btn"
                  >
                    {loading ? (
                      <div className="spinner w-5 h-5 border-2 border-blue-950 border-t-transparent" />
                    ) : (
                      <>
                        Start Check
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="admin">
                <form onSubmit={handleAdminLogin} className="space-y-6">
                  <div className="form-group">
                    <label htmlFor="admin-username">Username</label>
                    <Input
                      id="admin-username"
                      data-testid="admin-username"
                      placeholder="Enter username"
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                      className="h-12"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="admin-password">Password</label>
                    <Input
                      id="admin-password"
                      data-testid="admin-password"
                      type="password"
                      placeholder="Enter password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      className="h-12"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-blue-900 hover:bg-blue-800 font-bold text-lg btn-press"
                    disabled={loading}
                    data-testid="admin-login-btn"
                  >
                    {loading ? (
                      <div className="spinner w-5 h-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        Admin Login
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-sm text-slate-500">
        © {new Date().getFullYear()} City Cabs Buses & Coaches Ltd
      </footer>
    </div>
  );
}
